import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Alert
} from '@mui/material';
import { scheduleDescription } from '../../CronDescription';
import { logError } from 'helpers/logger';

export const DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' }
];

const MODES = [
  { value: 'hourly', label: 'Every hour in a window' },
  { value: 'daily', label: 'Once a day' },
  { value: 'weekly', label: 'Once on chosen days' },
  { value: 'custom', label: 'Custom cron' }
];

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

export const formatDays = (days) => {
  if (!Array.isArray(days)) {
    return '*';
  }
  const unique = [...new Set(days)].sort((a, b) => a - b);
  if (unique.length === 0 || unique.length === 7) {
    return '*';
  }
  const runs = [];
  for (const day of unique) {
    const last = runs[runs.length - 1];
    if (last && day === last[last.length - 1] + 1) {
      last.push(day);
    } else {
      runs.push([day]);
    }
  }
  return runs
    .map((run) =>
      run.length >= 3 ? `${run[0]}-${run[run.length - 1]}` : run.join(',')
    )
    .join(',');
};

export const parseDays = (field) => {
  if (typeof field !== 'string') {
    return null;
  }
  if (field === '*') {
    return ALL_DAYS;
  }
  const days = [];
  for (const part of field.split(',')) {
    const range = part.match(/^(\d)-(\d)$/);
    if (range) {
      const [, from, to] = range;
      for (let day = Number(from); day <= Number(to); day += 1) {
        days.push(day);
      }
    } else if (/^\d$/.test(part)) {
      days.push(Number(part));
    } else {
      return null;
    }
  }
  return days.length > 0 ? days : null;
};

const DEFAULT_STATE = {
  mode: 'hourly',
  minute: 0,
  startHour: 8,
  endHour: 17,
  hour: 6,
  days: [1, 2, 3, 4, 5]
};

export const parseSchedule = (schedule) => {
  try {
    return parseScheduleUnsafe(schedule);
  } catch (error) {
    logError('parseSchedule: could not read schedule', error, { schedule });
    return { ...DEFAULT_STATE, mode: 'custom' };
  }
};

const parseScheduleUnsafe = (schedule) => {
  if (!schedule || typeof schedule !== 'string' || schedule.trim() === '') {
    return { ...DEFAULT_STATE, mode: 'hourly' };
  }
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) {
    return { ...DEFAULT_STATE, mode: 'custom' };
  }
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  if (!/^\d{1,2}$/.test(minute) || dayOfMonth !== '*' || month !== '*') {
    return { ...DEFAULT_STATE, mode: 'custom' };
  }
  const days = parseDays(dayOfWeek);
  if (!days) {
    return { ...DEFAULT_STATE, mode: 'custom' };
  }

  const window = hour.match(/^(\d{1,2})-(\d{1,2})$/);
  if (window) {
    return {
      ...DEFAULT_STATE,
      mode: 'hourly',
      minute: Number(minute),
      startHour: Number(window[1]),
      endHour: Number(window[2]),
      days
    };
  }
  if (/^\d{1,2}$/.test(hour)) {
    return {
      ...DEFAULT_STATE,
      mode: dayOfWeek === '*' ? 'daily' : 'weekly',
      minute: Number(minute),
      hour: Number(hour),
      days
    };
  }
  return { ...DEFAULT_STATE, mode: 'custom' };
};

export const buildSchedule = (state) => {
  try {
    return buildScheduleUnsafe(state);
  } catch (error) {
    logError('buildSchedule: could not build schedule', error, { state });
    return null;
  }
};

const buildScheduleUnsafe = (state) => {
  const { mode, minute, startHour, endHour, hour, days } = state;
  if (mode === 'hourly') {
    return `${minute} ${startHour}-${endHour} * * ${formatDays(days)}`;
  }
  if (mode === 'daily') {
    return `${minute} ${hour} * * *`;
  }
  if (mode === 'weekly') {
    return `${minute} ${hour} * * ${formatDays(days)}`;
  }
  return null;
};

const timeValue = (hour, minute) =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

const hourOptions = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label: timeValue(hour, 0)
}));

export const CronScheduleCard = ({ useJobGeneralConfig }) => {
  const [schedule, setSchedule] = useJobGeneralConfig(['schedule']);
  const [runOnSchedule, setRunOnSchedule] = useJobGeneralConfig([
    'runOnSchedule'
  ]);
  const [builder, setBuilder] = useState(() => parseSchedule(schedule));

  const apply = (next) => {
    try {
      setBuilder(next);
      const built = buildSchedule(next);
      if (built !== null) {
        setSchedule(built);
      }
    } catch (error) {
      logError('CronScheduleCard: could not apply schedule', error, {
        next
      });
    }
  };

  const setMode = (mode) => {
    if (!mode) {
      return;
    }
    apply({ ...builder, mode });
  };

  const toggleDay = (day) => {
    const days = (builder.days || []).includes(day)
      ? builder.days.filter((d) => d !== day)
      : [...(builder.days || []), day];
    apply({ ...builder, days });
  };

  const setTime = (value) => {
    try {
      const [hour, minute] = String(value).split(':');
      apply({ ...builder, hour: Number(hour), minute: Number(minute) });
    } catch (error) {
      logError('CronScheduleCard: could not set time', error, { value });
    }
  };

  const describe = () => {
    try {
      return scheduleDescription(schedule || '');
    } catch (error) {
      logError('CronScheduleCard: could not describe schedule', error, {
        schedule
      });
      return 'Invalid schedule';
    }
  };

  const description = describe();
  const showDays = builder.mode === 'hourly' || builder.mode === 'weekly';
  const selectedDays = builder.days || [];

  return (
    <Card>
      <CardHeader subheader="When this job runs" title="Schedule" />
      <CardContent>
        <FormGroup>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!runOnSchedule}
                    onChange={(e, checked) => setRunOnSchedule(checked)}
                  />
                }
                label="Schedule active"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Frequency"
                onChange={(e) => setMode(e.target.value)}
                select
                // eslint-disable-next-line react/jsx-sort-props
                SelectProps={{ native: true }}
                value={builder.mode}
                variant="standard"
              >
                {MODES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
            </Grid>

            {builder.mode === 'hourly' && (
              <>
                <Grid item md={4} xs={12}>
                  <TextField
                    fullWidth
                    helperText="First run of the day"
                    label="From"
                    onChange={(e) =>
                      apply({ ...builder, startHour: Number(e.target.value) })
                    }
                    select
                    // eslint-disable-next-line react/jsx-sort-props
                    SelectProps={{ native: true }}
                    value={builder.startHour}
                    variant="standard"
                  >
                    {hourOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item md={4} xs={12}>
                  <TextField
                    fullWidth
                    helperText="Last run of the day"
                    label="To"
                    onChange={(e) =>
                      apply({ ...builder, endHour: Number(e.target.value) })
                    }
                    select
                    // eslint-disable-next-line react/jsx-sort-props
                    SelectProps={{ native: true }}
                    value={builder.endHour}
                    variant="standard"
                  >
                    {hourOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item md={4} xs={12}>
                  <TextField
                    fullWidth
                    helperText="Minutes past each hour"
                    inputProps={{ max: 59, min: 0 }}
                    label="At minute"
                    onChange={(e) =>
                      apply({ ...builder, minute: Number(e.target.value) })
                    }
                    type="number"
                    value={builder.minute}
                    variant="standard"
                  />
                </Grid>
              </>
            )}

            {(builder.mode === 'daily' || builder.mode === 'weekly') && (
              <Grid item md={4} xs={12}>
                <TextField
                  fullWidth
                  label="Time"
                  onChange={(e) => setTime(e.target.value)}
                  type="time"
                  value={timeValue(builder.hour, builder.minute)}
                  variant="standard"
                />
              </Grid>
            )}

            {showDays && (
              <Grid item xs={12}>
                <ToggleButtonGroup size="small" value={selectedDays}>
                  {DAYS.map((day) => (
                    <ToggleButton
                      key={day.value}
                      onClick={() => toggleDay(day.value)}
                      value={day.value}
                    >
                      {day.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
            )}

            {builder.mode === 'custom' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  helperText="Five-field cron expression"
                  label="Cron expression"
                  onChange={(e) => setSchedule(e.target.value)}
                  placeholder="0 8-17 * * 1-5"
                  value={schedule || ''}
                  variant="standard"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Alert
                severity={
                  description === 'Invalid schedule' ? 'error' : 'info'
                }
              >
                {description}
                {schedule ? ` (${schedule})` : ''}
              </Alert>
            </Grid>
          </Grid>
        </FormGroup>
      </CardContent>
    </Card>
  );
};

export default CronScheduleCard;
